import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn } from "lucide-react";
import { api, useAuth, formatApiError } from "@/App";

export default function AdminLogin() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white/5 border border-white/10 rounded-md px-5 py-4 text-sm text-[#F4F4F0] placeholder:text-[#A3A3A3]/60 outline-none focus:border-[#C44900] transition-colors duration-300";

  return (
    <div className="min-h-screen bg-[#031435] flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/" className="flex justify-center" data-testid="login-logo" aria-label="Edvio home">
          <img src="/edvio-logo-wide.png" alt="Edvio Language Institute" className="h-16 w-auto" />
        </Link>
        <p className="mt-2 text-center text-xs uppercase tracking-[0.25em] text-[#A3A3A3]">Admin Console</p>

        <form onSubmit={submit} className="mt-10 bg-[#081B40] border border-white/10 rounded-xl p-8 lg:p-10 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Email</label>
            <input type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@edvio.in" data-testid="admin-email-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Password</label>
            <input type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="admin-password-input" />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-[#C44900] text-[#F4F4F0] text-xs uppercase tracking-[0.2em] py-4 rounded-full hover:bg-[#e05600] transition-colors duration-300 disabled:opacity-60"
            data-testid="admin-login-submit-button"
          >
            {loading ? "Signing in…" : "Sign In"}
            {!loading && <LogIn size={14} />}
          </button>
        </form>
        <Link to="/" className="mt-6 block text-center text-xs text-[#A3A3A3] hover:text-[#F4F4F0] transition-colors" data-testid="login-back-home-link">
          ← Back to website
        </Link>
      </motion.div>
    </div>
  );
}
